import { Paper, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { StaffCersPrePostQuery } from "generated-graphql/graphql";
import { useMemo } from "react";

import { formatPct } from "./PrePostTable";

type AttributionRow = StaffCersPrePostQuery["staffCersPrePost"]["attribution"][number];

const SEGMENT_LABELS: Record<string, string> = {
  ENCAMP_FILED: "Encamp-filed",
  OUT_OF_BAND: "Out-of-band / unlinked",
};

export function AttributionTable({ rows, loading }: { rows: AttributionRow[]; loading: boolean }) {
  const columns: CustomGridColDef<AttributionRow>[] = useMemo(
    () => [
      {
        field: "segment",
        headerName: "Segment",
        flex: 2,
        minWidth: 160,
        valueFormatter: (value: string) => SEGMENT_LABELS[value] ?? value,
      },
      {
        field: "totalSubmittals",
        headerName: "Submittals",
        type: "number",
        flex: 1,
        minWidth: 100,
      },
      {
        field: "rejectedSubmittals",
        headerName: "Rejected",
        type: "number",
        flex: 1,
        minWidth: 90,
      },
      { field: "facilities", headerName: "Facilities", type: "number", flex: 1, minWidth: 90 },
      {
        field: "ratePct",
        headerName: "Rejection Rate",
        type: "number",
        flex: 1,
        minWidth: 110,
        valueFormatter: (value: number | null) => formatPct(value),
      },
    ],
    []
  );

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        After-Bucket Attribution
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Post-cutoff submittals split by whether they link to an Encamp report. Out-of-band filings
        are not Encamp&apos;s work and dilute the after-rate.
      </Typography>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={(row: AttributionRow) => row.segment}
        loading={loading}
        pagination
        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
        pageSizeOptions={[25, 50, 100]}
        disableColumnMenu
        disableRowSelectionOnClick
        noRowsMessage="No post-cutoff submittals."
        // Rows here are read-only aggregates — override the shared wrapper's
        // hover pointer so the cursor doesn't promise a click that does nothing.
        sx={{ "& .MuiDataGrid-row:hover": { cursor: "default" } }}
      />
    </Paper>
  );
}
